import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { ProcurementProjectLotDTO, CriteriaDTO, ProcurementProjectDTO, CompanyDTO } from '../../api/plyca-dtos-v2';
import { TenderDTO, ValuationCriteriaDTO, getSimpleIdentifierFromString, TenderLot } from '../../api/plyca-dtos';
import { PlycaDataService } from '../../../services/plyca.service';
import { WaitLoadingService } from '../../components/common/waitLoading/wait-loading.service';
import { finalize, tap, map, flatMap, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { UIService, generateRandomIdentifier } from '../../ui.service';
import { Color } from 'chart.js';
import * as lodash from 'lodash';
import { HelpService, HelpType } from '../../components/common/help/help.service';
import { HeaderActionsService } from '../../components/common/sub-header/header-actions.service';
import { Configuration } from '../../conf/app.constants';
import { PaginationDTO } from '../../api/PaginationModel';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng-lts/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '../../components/common/baseComponent/base.component';

export class CriteriaEditionBean {
  /**
   * Criterio que se está editando
   */
  criteria: CriteriaDTO;
  /**
   * Oferta del criterio a valorar
   */
  tender: TenderDTO;
  /**
   * Copia de la valoración de la oferta y criterio anteriores
   */
  value: ValuationCriteriaDTO;
}

/**
 * Tipo de visualización de tabla de criterios EMPRESAS por fila o CRITERIOS por fila
 */
export enum CRITERIA_TABLE_MODE {
  COMPANY,
  CRITERIA
};

@Component({
  selector: 'tenderCriteria',
  templateUrl: 'tender-criteria.template.html'
})
export class TenderCriteriaComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  public project: ProcurementProjectDTO;
  @Input()
  public tenderList: Array<TenderDTO>;
  @Output()
  public criteriaUpdated = new EventEmitter<TenderDTO>();

  public loaded = false;
  public criteriaWindowHash: any;
  public windowHash: any;
  public projectHasLots: boolean
  // listado de criterios de adjudicacion del expediente
  private lotAwardCriteriaList: Array<CriteriaDTO>;

  // bean de edición de un criterio
  public editingCriteria: CriteriaEditionBean;
  public editingCriteriaVisible = false;

  // datos de criterios por lote
  public lotCriteriaData: Map<number, CriteriaEditionBean[][]>;

  // graficos
  chartData: any;
  chartType: string;
  chartVisible = false;
  public charTitle: string;

  public CRITERIA_TABLE_MODE = CRITERIA_TABLE_MODE;
  public selectedCriteriaTableMode = CRITERIA_TABLE_MODE.COMPANY;

  private previousHelpTags: string[] = [];

  constructor(private plycaService: PlycaDataService,
    public uiService: UIService,
    private helpService: HelpService,
    private headerActionsService: HeaderActionsService,
    private configuration: Configuration,
    private waitService: WaitLoadingService,
    protected dialogConfig: DynamicDialogConfig,
    public translateService: TranslateService,
    protected dialogRef: DynamicDialogRef) {
    super();
  }

  ngOnInit(): void {
    let projectObs: Observable<ProcurementProjectDTO>;
    let dialogOpened = this.dialogConfig && this.dialogConfig.data;
    if (dialogOpened) {
      // apertura en dialogo
      this.project = this.dialogConfig.data.project;
      this.windowHash = generateRandomIdentifier();
      // cabecera ventana criterios
      this.headerActionsService.clearWindowActions(this.windowHash);
      this.subscribe(this.headerActionsService
        .addWindowAction(
          'General.cancel',
          'fas fa-lg fa-times',
          'btn-outline-secondary',
          true,
          this.windowHash
        ),
        success => {
          this.dialogRef.close();
        });
    }
    // cabecera edición de un criterio
    this.criteriaWindowHash = generateRandomIdentifier();
    this.headerActionsService.clearWindowActions(this.criteriaWindowHash);
    this.subscribe(this.headerActionsService
      .addWindowAction(
        'General.accept',
        'fa fa-lg fa-save',
        'btn-primary',
        true,
        this.criteriaWindowHash
      ),
      success => {
        this.saveEditingTenderCriteria();
      });
    this.subscribe(this.headerActionsService
      .addWindowAction(
        'General.cancel',
        'fas fa-lg fa-times',
        'btn-outline-secondary',
        true,
        this.criteriaWindowHash
      ),
      success => {
        this.cancelEditingTenderCriteria();
      });
    // TODO en caso de apertura en modal se vuelve a consular todo
    if (this.project && (!this.project.breakdownLot || dialogOpened)) {
      // consultar lotes si no vienen cargados
      projectObs = this.plycaService.getProcurementProject(this.project.identifier, '?expand=expansionSet:subclass_complete').pipe(
        tap((project: ProcurementProjectDTO) => this.project.breakdownLot = project.breakdownLot)
      );
    } else {
      projectObs = of(this.project);
    }
    let tenderObs = this.readTenders().pipe(
      map(() => this.project)
    );
    this.subscribe(projectObs.pipe(
      flatMap((project: ProcurementProjectDTO) => {
        if (dialogOpened) {
          return tenderObs;
        } else {
          return of(project);
        }
      }),
      tap((project: ProcurementProjectDTO) => this.processProjectCriteria()),
      finalize(() => this.waitService.cancelWaiting())
    ),
      () => undefined,
      () => undefined,
      () => this.loaded = true);
  }

  ngOnDestroy() {
    this.headerActionsService.clearWindowActions(this.windowHash);
    this.dialogConfig = null;
    this.dialogRef = null;
    super.ngOnDestroy();
  }

  private readTenders(): Observable<PaginationDTO<TenderDTO>> {
    return this.plycaService.getDossierTenders(this.project.identifier).pipe(
      tap((result: PaginationDTO<TenderDTO>) => {
        if (result && result.content) {
          this.tenderList = result.content;
        }
      })
    );
  }

  private processProjectCriteria() {
    this.projectHasLots = this.project.breakdownLot.apportioned;
    // PROCESAR LISTADO DE CRITERIOS
    let criteriaList = new Array<CriteriaDTO>();
    if (this.projectHasLots) {
      this.project.breakdownLot.lots.forEach((lot: ProcurementProjectLotDTO) => {
        // procesar criterios
        if (lot.selectionAwardCriteria) {
          const lotRef = this.getLotRefObject(lot);
          lot.selectionAwardCriteria.forEach((criteria: CriteriaDTO) => {
            criteria.lot = lotRef;
            criteriaList.push(criteria);
          });
        }
      });
    } else {
      // anyadimos a la lista de criterios todos los criterios del lote 0
      if (this.project.breakdownLot.totalLots.selectionAwardCriteria) {
        const lotRef = this.getLotRefObject(this.project.breakdownLot.totalLots);
        this.project.breakdownLot.totalLots.selectionAwardCriteria.forEach((criteria: CriteriaDTO) => {
          criteria.lot = lotRef;
          criteriaList.push(criteria);
        });
      }
    }
    this.lotAwardCriteriaList = criteriaList;
    this.initCriteriaTableData()
  }

  private getLotRefObject(lot: ProcurementProjectLotDTO) {
    const lotRef = new ProcurementProjectLotDTO();
    lotRef.identifier = lot.identifier;
    lotRef.lotNumber = lot.lotNumber;
    lotRef.lotId = lot.lotId;
    return lotRef;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tenderList) {
      this.processProjectCriteria();
    }
  }

  public getLots(): Array<ProcurementProjectLotDTO> {
    let lots: Array<ProcurementProjectLotDTO>;
    if (this.projectHasLots) {
      lots = this.project.breakdownLot.lots;
    } else {
      lots = [];
      lots.push(this.project.breakdownLot.totalLots);
    }
    return lots.sort((lot1: ProcurementProjectLotDTO, lot2: ProcurementProjectLotDTO) => lot1.lotNumber - lot2.lotNumber);
  }

  private cancelEditingTenderCriteria(): void {
    this.editingCriteriaVisible = false;
    this.editingCriteria = undefined;
    this.waitService.cancelWaiting();

  }

  private saveEditingTenderCriteria(): void {
    this.editingCriteriaVisible = false;
    this.waitService.startWaiting();

    let prevTenderCriteria = this.editingCriteria.tender.valuationCriteria
      .find((tenderCriteriaValue: ValuationCriteriaDTO) => tenderCriteriaValue.criteriaId === this.editingCriteria.criteria.identifier);
    if (prevTenderCriteria && prevTenderCriteria.tenderValue !== this.editingCriteria.value.tenderValue) {
      let patchedTender = new TenderDTO();
      patchedTender.identifier = this.editingCriteria.tender.identifier;
      patchedTender.valuationCriteria = [];
      let tenderCriteriaValue = new ValuationCriteriaDTO()
      patchedTender.valuationCriteria.push(tenderCriteriaValue);
      tenderCriteriaValue.criteriaId = this.editingCriteria.value.criteriaId;
      tenderCriteriaValue.lot = new ProcurementProjectLotDTO();
      tenderCriteriaValue.lot.identifier = this.editingCriteria.criteria.lot.identifier;
      tenderCriteriaValue.lot.lotNumber = this.editingCriteria.criteria.lot.lotNumber;
      tenderCriteriaValue.tenderValue = this.editingCriteria.value.tenderValue;

      let url = this.configuration.TENDER_SERVICE_URL;
      this.subscribe(this.plycaService
        .patch<TenderDTO>(url, patchedTender)
        .pipe(
          finalize(() => this.cancelEditingTenderCriteria())
        ),
        () => {
          if (this.windowHash) {
            this.waitService.startWaiting();
            this.subscribe(this.readTenders().pipe(
              tap(() => this.processProjectCriteria()),
              finalize(() => this.waitService.cancelWaiting())
            ));
          } else {
            // emitimos actualizacion para que la ventana que añade este componente actualice las ofertas
            this.criteriaUpdated.emit(patchedTender);
          }
        });
    } else {
      this.cancelEditingTenderCriteria();
    }
  }

  private getFilteredLotData(allData: CriteriaEditionBean[][], lot: ProcurementProjectLotDTO): CriteriaEditionBean[][] {
    return this.filterCriteriaRowData(allData, lot).map((row: CriteriaEditionBean[]) => this.filterCriteriaColData(row, lot));
  }

  /**
   * Filtrar datos en funcion de si la oferta licita el lote
   * @param allData
   * @param lot
   * @returns
   */
  private filterCriteriaRowData(allData: CriteriaEditionBean[][], lot: ProcurementProjectLotDTO): CriteriaEditionBean[][] {
    return allData
      .filter((row: CriteriaEditionBean[]) => (!this.projectHasLots ||
        (
          (this.selectedCriteriaTableMode === CRITERIA_TABLE_MODE.COMPANY &&
            this.tenderIncludesLot(row[0].tender, lot))) ||
        (this.selectedCriteriaTableMode === CRITERIA_TABLE_MODE.CRITERIA &&
          row[0].criteria.lot.lotNumber === lot.lotNumber)
      )
      )
      .sort((critObj1: CriteriaEditionBean[], critObj2: CriteriaEditionBean[]) => {
        let score1 = this.getSumCriteria(this.filterCriteriaColData(critObj1, lot));
        let score2 = this.getSumCriteria(this.filterCriteriaColData(critObj2, lot));
        if (score1 > score2) {
          return -1;
        } else if (score1 < score2) {
          return 1;
        } else {
          return 0;
        }
      });
  }


  public getSumCriteria(criteriaList: CriteriaEditionBean[]): number {
    let sumatory = 0;
    if (criteriaList) {
      criteriaList
        .map(criteria => criteria.value)
        .forEach(valoration => sumatory += (valoration) ? valoration.weightedScoreValue : 0);
    }
    return sumatory;
  }

  /**
   * Filtrar columnas en función de si el criterio está asociado al lote
   * @param rowData
   * @param lot
   * @returns
   */
  private filterCriteriaColData(rowData: CriteriaEditionBean[], lot: ProcurementProjectLotDTO): CriteriaEditionBean[] {
    return rowData.filter((col: CriteriaEditionBean) => (!this.projectHasLots ||
      (
        (this.selectedCriteriaTableMode === CRITERIA_TABLE_MODE.COMPANY &&
          col.criteria.lot.lotNumber === lot.lotNumber) ||
        (this.selectedCriteriaTableMode === CRITERIA_TABLE_MODE.CRITERIA &&
          this.tenderIncludesLot(col.tender, lot))
      )));
  }

  public showTenderValorationGraph(rowData: Array<CriteriaEditionBean>) {
    this.chartType = 'pie';
    let labels = [];
    let data = [];
    let totalScore = 0;
    rowData.forEach((criteria: CriteriaEditionBean) => {
      labels.push(criteria.criteria.name);
      let score = criteria.value.weightedScoreValue;
      score = (score) ? score : 0;
      totalScore += score;
      data.push(score);
    });
    let companyDescription = CompanyDTO.getCompanyDescription(rowData[0].tender.company);
    this.chartData = {
      labels: labels,
      datasets: [
        {
          label: companyDescription,
          data: data
        }
      ]
    };
    // establecer colores por defecto
    this.setDefaultChartViewParams(this.chartData);
    this.charTitle = companyDescription;
    this.chartVisible = true;
  }

  private tenderIncludesLot(tender: TenderDTO, lot: ProcurementProjectLotDTO): boolean {
    return tender.tendersLots
      && tender.tendersLots
        .find((tenderLot: TenderLot) =>
          getSimpleIdentifierFromString(tenderLot.identifier) === lot.lotId.toString()
        ) !== undefined;
  }

  public showCriteriaValorationGraph(tableData: any, index: number) {
    this.chartType = 'bar';
    let labels = [];
    let data = [];
    tableData.forEach(row => {
      labels.push(CompanyDTO.getCompanyDescription((row[index].tender.company)));
      data.push(row[index].value.weightedScoreValue);
    });
    let criteriaDesc = tableData[0][index]?.criteria?.description;
    this.chartData = {
      labels: labels,
      datasets: [
        {
          label: criteriaDesc,
          data: data
        }
      ]
    };
    // establecer colores por defecto
    this.setDefaultChartViewParams(this.chartData);
    this.charTitle = tableData[0][index]?.criteria?.name;
    this.chartVisible = true;
  }

  private setDefaultChartViewParams(chartData: any): void {
    if (chartData && chartData.datasets) {
      const datasets = chartData.datasets;
      const options: any = {
        responsive: false,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              min: 0
            }
          }],
          xAxes: [{
            ticks: {
              beginAtZero: true,
              min: 0
            }
          }]
        }
      };
      if (datasets) {
        datasets.forEach((dataset: any) => {
          // colores
          if (!dataset.backgroundColor) {
            Object.assign(dataset, this.uiService.chartColorsDef);
          }
          // comenzar en 0
          Object.assign(chartData.datasets[0], options);
        })
      }
    }
  }

  // metodos de carga de tabla de criterios

  /**
   * Establecer el contenido de la tabla de criterios en función de las ofertas filtradas y los criterios del
   * expediente.
   * Cada fila representa una oferta, y cada columna un criterio para dicha oferta
   */
  private initCriteriaTableData(): void {
    let criteriaTableData = (this.selectedCriteriaTableMode === CRITERIA_TABLE_MODE.COMPANY) ?
      this.getCriteriaTableDataByCompany()
      :
      this.getCriteriaTableDataByCriteria();
    let lots = this.getLots();
    this.lotCriteriaData = new Map();
    // generar datos de cada tabla por lote
    lots.forEach((lot: ProcurementProjectLotDTO) => {
      this.lotCriteriaData.set(lot.lotId, this.getFilteredLotData(criteriaTableData, lot));
    })
  }

  private getCriteriaTableDataByCompany(): CriteriaEditionBean[][] {
    let auxTableData = [];
    let filteredCriteriaList = this.sortCriteriaList(this.lotAwardCriteriaList);
    this.tenderList.forEach((tender: TenderDTO) => {
      let companyRow = new Array<CriteriaEditionBean>();
      auxTableData.push(companyRow);
      filteredCriteriaList.forEach((val: CriteriaDTO) => {
        let criteriaBean = new CriteriaEditionBean();
        criteriaBean.tender = tender;
        criteriaBean.criteria = val;
        criteriaBean.value = this.getTenderCriteriaValue(tender, val);
        companyRow.push(criteriaBean);
      });
    });
    return auxTableData;
  }

  private sortCriteriaList(criteriaList: Array<CriteriaDTO>): Array<CriteriaDTO> {
    return criteriaList
      .sort((criteria1: CriteriaDTO, criteria2: CriteriaDTO) => criteria1.lot.lotNumber - criteria2.lot.lotNumber)
      .sort((criteria1: CriteriaDTO, criteria2: CriteriaDTO) =>
        this.booleanToNumber(criteria1.awardingCriteriaTypeCode) - this.booleanToNumber(criteria2.awardingCriteriaTypeCode))
      .sort((criteria1: CriteriaDTO, criteria2: CriteriaDTO) => ('' + criteria1.name).localeCompare(criteria2.name))
  }

  private booleanToNumber(value: boolean): number {
    return (value) ? 1 : 0;
  }

  private getCriteriaTableDataByCriteria(): CriteriaEditionBean[][] {
    let auxTableData = [];
    let filteredCriteriaList = this.sortCriteriaList(this.lotAwardCriteriaList);
    filteredCriteriaList.forEach((val: CriteriaDTO) => {
      let criteriaRow = new Array<CriteriaEditionBean>();
      auxTableData.push(criteriaRow);
      this.tenderList.forEach((tender) => {
        let criteriaBean = new CriteriaEditionBean();
        criteriaBean.tender = tender;
        criteriaBean.criteria = val;
        criteriaBean.value = this.getTenderCriteriaValue(tender, val);
        criteriaRow.push(criteriaBean);
      });
    });
    return auxTableData;
  }
  private getTenderCriteriaValue(tender: TenderDTO, val: CriteriaDTO): ValuationCriteriaDTO {
    if (!tender.valuationCriteria) {
      tender.valuationCriteria = [];
    }
    let criteriaValue = tender.valuationCriteria
      .find((tenderCriteriaValue: ValuationCriteriaDTO) => tenderCriteriaValue.criteriaId === val.identifier);
    if (criteriaValue === undefined) {
      criteriaValue = new ValuationCriteriaDTO();
      criteriaValue.criteriaId = val.identifier;
      tender.valuationCriteria.push(criteriaValue);
    }
    ValuationCriteriaDTO.patchDTO(criteriaValue);
    return criteriaValue;
  }

  public getCompanyDescription(tender: TenderDTO): string {
    return CompanyDTO.getCompanyDescription(tender.company);
  }

  public editTenderCriteria(editingCriteria: CriteriaEditionBean) {
    // editar un objeto igual al de la tabla pero con una copia del valor para poder cancelar
    this.editingCriteria = new CriteriaEditionBean();
    this.editingCriteria.tender = editingCriteria.tender;
    this.editingCriteria.criteria = editingCriteria.criteria;
    this.editingCriteria.value = lodash.cloneDeep(editingCriteria.value);
    this.editingCriteriaVisible = true;
    this.helpContext();
  }

  private helpContext() {
    this.subscribe(this.helpService.getStoreTagsObservable().pipe(
      switchMap(
        (storedHelpTags) => {
          this.previousHelpTags = storedHelpTags;
          return this.helpService.showContextHelpObservable(HelpType.EDIT_VAL_CRITERIO)
        }
      )));
  }

  public closeModal() {
    this.subscribe(this.helpService.showContextHelpObservable(this.previousHelpTags));
  }

  /**
     * Validar que lo tecleado está en rango
     * @param event
     *
     */
  public setTenderValue(event: any): void {
    let value = event.currentTarget.value;
    if (!this.editingCriteria.criteria.awardingCriteriaTypeCode && value > this.editingCriteria.criteria.weight) {
      // valor invalido cancelar
      this.uiService.cancelEvent(event);
      // reestablecer valor anteiror
      event.currentTarget.value = this.editingCriteria.value.tenderValue
    } else {
      // valor valido
      this.editingCriteria.value.tenderValue = value;
    }
  }

}
